/**
 * "What's new" (#3315).
 *
 * - Test environment: everything this test version has that production does
 *   not, i.e. what the customer is about to approve (#3316).
 * - Production: what changed since the version this user saw before on this
 *   device. Without one there is nothing to compare with, and the dialog says
 *   so instead of guessing.
 */

import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { formatDateTime } from '@/utils/locale';
import { groupReleaseNotes } from './groupReleaseNotes';
import { useVersionAckKey, useVersionChanges, useVersionInfo } from './useVersionInfo';
import type { VersionChanges, VersionInfo } from './versionApi';
import { readVersionAck } from './versionNotice';
import { useWhatsNewStore } from './whatsNewStore';

export function WhatsNewDialog() {
  const { t } = useTranslation('releases');
  const open = useWhatsNewStore((s) => s.open);
  const close = useWhatsNewStore((s) => s.closeWhatsNew);

  return (
    <ResponsiveDialog open={open} onClose={close} fullWidth maxWidth="sm" mobileTitle={t('whatsNew.title')}>
      {open && <WhatsNewContent onClose={close} />}
    </ResponsiveDialog>
  );
}

function WhatsNewContent({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('releases');
  const { t: tc } = useTranslation('common');
  const info = useVersionInfo();
  const ackKey = useVersionAckKey();

  const isTest = info.data?.environment === 'test';
  const previousSha = !isTest && ackKey !== null ? readVersionAck(window.localStorage, ackKey)?.previousSha ?? null : null;
  // Test: the server's default baseline is production. Production: the
  // version seen before this one, when known.
  const compareEnabled = info.data !== undefined && (isTest || previousSha !== null);
  const changes = useVersionChanges({
    ...(isTest || previousSha === null ? {} : { from: previousSha }),
    enabled: compareEnabled,
  });

  const title = isTest
    ? info.data?.production
      ? t('whatsNew.testTitle', { version: info.data.production.version })
      : t('whatsNew.testTitleUnknownProduction')
    : t('whatsNew.title');

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {info.isLoading && <Loading />}
        {info.isError && <Alert severity="error">{t('whatsNew.loadError')}</Alert>}
        {info.data && (
          <Stack spacing={2}>
            <VersionSummary info={info.data} />
            {!compareEnabled ? (
              <Alert severity="info">{t('whatsNew.noPreviousVersion')}</Alert>
            ) : changes.isLoading ? (
              <Loading />
            ) : changes.isError ? (
              <Alert severity="error">{t('whatsNew.loadError')}</Alert>
            ) : changes.data ? (
              <ChangeList changes={changes.data} isTest={isTest} hasProduction={info.data.production !== null} />
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tc('close')}</Button>
      </DialogActions>
    </>
  );
}

function Loading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
      <CircularProgress size={28} />
    </Box>
  );
}

function VersionSummary({ info }: { info: VersionInfo }) {
  const { t } = useTranslation('releases');
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">
        {t('whatsNew.version')}: <strong>{info.version}</strong>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('whatsNew.installedAt')}: {formatDateTime(info.installedAt)}
      </Typography>
      {info.environment === 'test' && info.production && (
        <Typography variant="body2" color="text.secondary">
          {t('whatsNew.productionVersion')}: {info.production.version}
        </Typography>
      )}
    </Stack>
  );
}

function ChangeList({
  changes,
  isTest,
  hasProduction,
}: {
  changes: VersionChanges;
  isTest: boolean;
  hasProduction: boolean;
}) {
  const { t, i18n } = useTranslation('releases');

  if (changes.status === 'unavailable') {
    return (
      <Alert severity="info">
        {isTest && !hasProduction ? t('whatsNew.productionUnavailable') : t('whatsNew.unavailable')}
      </Alert>
    );
  }
  if (changes.status !== 'ok') {
    return <Alert severity="info">{t('whatsNew.unknownVersion')}</Alert>;
  }

  const groups = groupReleaseNotes(changes.entries, i18n.language);
  if (groups.length === 0) {
    return <Typography color="text.secondary">{t('whatsNew.noChanges')}</Typography>;
  }

  return (
    <Stack spacing={2}>
      {groups.map((group) => (
        <Box key={group.category} component="section" aria-labelledby={`whats-new-${group.category}`}>
          <Typography id={`whats-new-${group.category}`} variant="subtitle1" fontWeight={600}>
            {t(`whatsNew.categories.${group.category}`)}
          </Typography>
          <List dense disablePadding>
            {group.notes.map((note) => (
              <ListItem key={note.id} disableGutters alignItems="flex-start">
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <span>{note.title}</span>
                      {note.actionRequired && (
                        <Chip size="small" color="warning" label={t('whatsNew.actionRequired')} />
                      )}
                    </Stack>
                  }
                  secondary={note.body}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      ))}
    </Stack>
  );
}
